import { useAuth } from "@/_core/hooks/useAuth";
import { ClientLogo } from "@/components/ClientLogo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { ArrowLeft, Plus, LogOut } from "lucide-react";
import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { api } from "@/lib/api";
import { ModalPrimaryAction } from "@/components/ModalPrimaryAction";
import { PAGE_TITLE_STANDALONE_CLASS } from "@/lib/modalStyles";

const OPCAO_OUTRA = "__outra__";

const UNIDADES = [
  { value: "un", label: "Unidade (un)" },
  { value: "kg", label: "Quilograma (kg)" },
  { value: "g", label: "Grama (g)" },
  { value: "L", label: "Litro (L)" },
  { value: "ml", label: "Mililitro (ml)" },
  { value: "dz", label: "Dúzia (dz)" },
  { value: "caixa", label: "Caixa" },
  { value: "pacote", label: "Pacote" },
  { value: "maço", label: "Maço" },
];

export default function CadastroInsumo() {
  const { user, logout } = useAuth();
  const [, setLocation] = useLocation();
  const [nome, setNome] = useState("");
  const [categorias, setCategorias] = useState<string[]>([]);
  const [categoriaSelect, setCategoriaSelect] = useState<string>("");
  const [categoriaOutra, setCategoriaOutra] = useState("");
  const [unidade, setUnidade] = useState("");
  const [estoqueMinimo, setEstoqueMinimo] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadingCategorias, setLoadingCategorias] = useState(true);

  const isAdmin = user?.role === "admin";
  const allowedTabs = user?.allowed_tabs || null;
  const canAccessCadastroInsumo = isAdmin || (Array.isArray(allowedTabs) && allowedTabs.includes("insumos"));

  useEffect(() => {
    (async () => {
      try {
        setLoadingCategorias(true);
        const lista = await api.listarCategorias();
        setCategorias(lista.map((c) => c.nome));
      } catch (e) {
        console.error(e);
        alert("Erro ao carregar categorias");
      } finally {
        setLoadingCategorias(false);
      }
    })();
  }, []);

  if (!canAccessCadastroInsumo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Acesso Negado</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              Você não tem permissão para acessar esta página.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const categoriaFinal =
    categoriaSelect === OPCAO_OUTRA ? categoriaOutra.trim() : categoriaSelect;
  const showCadastroAction = nome.trim() !== "" && categoriaFinal !== "" && unidade.trim() !== "";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome || !unidade) {
      alert("Preencha todos os campos obrigatórios");
      return;
    }
    if (!categoriaFinal) {
      alert(
        categoriaSelect === OPCAO_OUTRA
          ? "Digite a nova categoria ou escolha uma existente."
          : "Selecione ou digite uma categoria."
      );
      return;
    }
    if (categoriaSelect === OPCAO_OUTRA && !isAdmin) {
      alert("Apenas administradores podem criar novas categorias.");
      return;
    }

    try {
      setSaving(true);
      await api.criarInsumo({
        nome,
        categoria: categoriaFinal,
        unidade,
        estoqueMinimo: parseFloat(estoqueMinimo) || 0,
      });
      setSuccessMessage("Insumo cadastrado com sucesso!");
      setNome("");
      setCategoriaSelect("");
      setCategoriaOutra("");
      setUnidade("");
      setEstoqueMinimo("");
      if (categoriaSelect === OPCAO_OUTRA && categoriaFinal) {
        setCategorias((prev) => (prev.includes(categoriaFinal) ? prev : [...prev, categoriaFinal].sort()));
      }
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      alert("Erro ao cadastrar insumo");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Header */}
      <div className="bg-accent text-accent-foreground sticky top-0 z-50 shadow-lg">
        <div className="container max-w-4xl mx-auto px-4 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLocation("/dashboard")}
              className="text-accent-foreground hover:bg-accent/80 flex-shrink-0"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="min-w-0 flex-1 flex flex-col justify-center">
              <ClientLogo variant="menu" className="h-8 sm:h-10 w-auto object-contain" />
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            className="text-accent-foreground hover:bg-accent/80 flex-shrink-0"
          >
            <LogOut className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Sair</span>
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="container max-w-4xl mx-auto px-4 py-6 pb-20">
        <h1 className={`mb-4 ${PAGE_TITLE_STANDALONE_CLASS}`}>Cadastrar Insumo</h1>
        <Card>
          <CardHeader>
            <CardDescription>
              Adicione um novo insumo ao sistema de controle de estoque
            </CardDescription>
          </CardHeader>
          <CardContent>
            {successMessage && (
              <div className="mb-4 p-4 bg-green-100 text-green-800 rounded-lg border border-green-200">
                {successMessage}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome do Insumo *</Label>
                <Input
                  id="nome"
                  placeholder="Ex: Café em Grão Premium"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  className="h-12 text-lg w-full"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="categoria">Categoria *</Label>
                <Select
                  id="categoria"
                  value={categoriaSelect}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setCategoriaSelect(e.target.value)
                  }
                  className="h-12 text-lg w-full"
                  required={categoriaSelect !== OPCAO_OUTRA}
                >
                  <option value="">Selecione uma categoria</option>
                  {categorias.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                  {isAdmin && (
                    <option value={OPCAO_OUTRA}>Outra (digitar categoria)</option>
                  )}
                </Select>
                {categoriaSelect === OPCAO_OUTRA && isAdmin && (
                  <div className="pt-2">
                    <Label htmlFor="categoria-outra">Nome da nova categoria *</Label>
                    <Input
                      id="categoria-outra"
                      placeholder="Ex: Limpeza"
                      value={categoriaOutra}
                      onChange={(e) => setCategoriaOutra(e.target.value)}
                      className="h-12 text-lg w-full mt-1"
                    />
                  </div>
                )}
                {loadingCategorias && (
                  <p className="text-sm text-muted-foreground">Carregando categorias...</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="unidade">Unidade de Medida *</Label>
                <Select 
                  id="unidade"
                  value={unidade} 
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setUnidade(e.target.value)}
                  className="h-12 text-lg w-full"
                  required
                >
                  <option value="">Selecione a unidade</option>
                  {UNIDADES.map((un) => (
                    <option key={un.value} value={un.value}>
                      {un.label}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="estoqueMinimo">Estoque Mínimo</Label>
                <Input
                  id="estoqueMinimo"
                  type="number"
                  step="0.1"
                  min="0"
                  placeholder="Ex: 10"
                  value={estoqueMinimo}
                  onChange={(e) => setEstoqueMinimo(e.target.value)}
                  className="h-12 text-lg w-full"
                />
                <p className="text-sm text-muted-foreground">
                  Quantidade mínima para alerta de compra
                </p>
              </div>

              <ModalPrimaryAction
                show={showCadastroAction}
                type="submit"
                loading={saving}
                disabled={saving}
              >
                {saving ? "Cadastrando..." : (
                  <>
                    <Plus className="mr-2 h-5 w-5" />
                    Cadastrar Insumo
                  </>
                )}
              </ModalPrimaryAction>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
